import {
  BIndexThresholdType,
  type SearchQuestionItemAnalysisRecordsQuery,
  useSearchQuestionItemAnalysisRecordsQuery,
} from '@report/graphql';
import { dayjs, type PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

import { useItemAnalysisSearchParams } from '../../hooks';

interface ItemAnalysisContextData {
  questionAnalysisData?: SearchQuestionItemAnalysisRecordsQuery['reportSearchQuestionItemAnalysisRecords'];
  searchItemAnalysisLoading?: boolean;
  isFilterSet?: boolean;
}

export const ItemAnalysisContext = createContext<ItemAnalysisContextData | undefined>(undefined);
export default function ItemAnalysisProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;
  const [
    {
      startDate,
      endDate,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
      page,
      pageSize,
    },
  ] = useItemAnalysisSearchParams();

  const isFilterSet =
    !!startDate &&
    !!endDate &&
    !!examCourse &&
    questionStatus.length > 0 &&
    minExaminees !== null &&
    knowledgeGroupSelection !== null &&
    (knowledgeGroupSelection !== BIndexThresholdType.CustomThreshold ||
      knowledgeGroupCustomThreshold != null) &&
    nonKnowledgeGroupSelection !== null &&
    (nonKnowledgeGroupSelection !== BIndexThresholdType.CustomThreshold ||
      nonKnowledgeGroupCustomThreshold != null);

  const { data: searchData, loading: searchItemAnalysisLoading } =
    useSearchQuestionItemAnalysisRecordsQuery({
      variables: {
        searchQuestionItemAnalysisRecordsInput: {
          startAt: dayjs(startDate ?? new Date())
            .startOf('day')
            .toDate(),
          questionStatus: questionStatus,
          qBankStructureId: examCourse ?? '',
          paging: {
            pageSize: pageSize,
            currentPage: page,
          },
          nonKnowledgeGroup: {
            value: nonKnowledgeGroupCustomThreshold ?? undefined,
            type: nonKnowledgeGroupSelection ?? null,
          },
          minimumExamineePerQuestion: minExaminees ?? 0,
          knowledgeGroup: {
            value: knowledgeGroupCustomThreshold ?? undefined,
            type: knowledgeGroupSelection ?? null,
          },
          endAt: dayjs(endDate ?? new Date())
            .endOf('day')
            .toDate(),
        },
      },
      skip: !isFilterSet,
    });

  const contextValue: ItemAnalysisContextData = useMemo(
    () => ({
      questionAnalysisData: searchData?.reportSearchQuestionItemAnalysisRecords,
      searchItemAnalysisLoading,
      isFilterSet,
    }),
    [searchData, searchItemAnalysisLoading, isFilterSet],
  );

  return (
    <ItemAnalysisContext.Provider value={contextValue}>{children}</ItemAnalysisContext.Provider>
  );
}
